// GA4 tracking for the collection-page quick redirect row — the "Redirect test
// for inactive users" experiment (Convert experiment 1004212174). Two events:
//
//   category_navigation_visible  the row came fully into view
//   quick_redirect               a collection in the row was tapped
//
// WHY THIS WATCHES THE DOM
// The row is not theme markup. Convert's variant code builds it in the browser
// and inserts it right before .collection-grid__header, and only for visitors
// bucketed into the variant — on a first visit only once they scroll past the
// first two rows of product cards. So it can appear at any point after load, or
// never, which is also what keeps both events variant-only.
//
//   div#dk-skin-nav
//     a.dk-skin-nav__pill                         one per collection
//     a.dk-skin-nav__pill.dk-skin-nav__pill--active  the one being viewed
//
// Every value is read off the page, nothing is kept here:
//   cta_text              the tapped link's text
//   current_select_value  the active link's text
//   category              the site menu group those collections sit under
//
// The row does not say which group it shows, but the header menu does: the same
// collections are listed there under Skin Concern / Ingredient / Skin Type. So
// the row's links are looked up in the menu and the group holding most of them
// names the category. Editing the menu in the Shopify admin carries through with
// no code change.
(() => {
  'use strict';

  const CLIENT_ID_STORAGE_KEY = 'themex-client-id';

  const NAV_ID = 'dk-skin-nav';
  const PILL_SELECTOR = '.dk-skin-nav__pill';
  const ACTIVE_PILL_SELECTOR = '.dk-skin-nav__pill--active';
  const SECTION_SELECTOR = '[data-cg-section]';

  // A little under 1 so a sub-pixel rounding of the row's box does not stop the
  // event from ever firing.
  const FULLY_VISIBLE_RATIO = 0.99;

  // The spec marks both events "Mweb Only". Mobile is anything below the theme's
  // md breakpoint (768px, tailwind.config.js) — the same line md:hidden draws for
  // the theme's other mobile-only UI. Read at event time, so a rotation or a
  // resized window is respected.
  const MWEB_QUERY = '(max-width: 767px)';
  const isMweb = () => window.matchMedia(MWEB_QUERY).matches;

  // ─── client_id ────────────────────────────────────────────────────────────
  // Same resolution order as the theme's other tracking files
  // (shopall-drawer-tracking.js, pair-popup-tracking.js,
  // pdp-recently-viewed.js) so client_id agrees across every event: the GA4
  // client id off the _ga cookie, falling back to a self-generated id so a value
  // is always present.
  const generateId = () =>
    window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const getClientId = () => {
    try {
      const gaCookie = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (gaCookie) {
        const parts = decodeURIComponent(gaCookie[1]).split('.');
        if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
      }
    } catch (_) {
      // Malformed _ga cookie — fall through to the stored/generated id.
    }

    try {
      let clientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
      if (!clientId) {
        clientId = generateId();
        localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
      }
      return clientId;
    } catch (_) {
      // localStorage can throw (Safari private mode, quota, policy).
      return generateId();
    }
  };

  // ─── Reading the row ──────────────────────────────────────────────────────

  const readText = (element) => element?.textContent.trim() || null;

  // Menu and row mix relative and absolute hrefs for the same collection
  // (/collections/dry-skin vs https://www.dotandkey.com/collections/dry-skin),
  // so links are compared on path alone.
  const toPath = (href) => {
    try {
      return new URL(href, window.location.origin).pathname.replace(/\/$/, '').toLowerCase();
    } catch (_) {
      return null;
    }
  };

  // The menu group a header link sits under, or null if it is not in a group.
  //   Mobile drawer (snippets/header-drawer.liquid): the group's toggle button,
  //     aria-label "Skin Concern", directly precedes the list holding the link.
  //   Desktop (snippets/header-desktop.liquid): the dropdown's data-nav-dropdown
  //     index matches its trigger's data-nav-trigger, whose label is the title.
  // The drawer is read first because it carries the titles in the spec's own
  // casing; the desktop ones are uppercased (SKIN TYPE).
  const readDrawerGroup = (link) => {
    const toggle = link.closest('ul')?.previousElementSibling;
    if (!toggle?.matches('[data-drawer-toggle]')) return null;
    return toggle.getAttribute('aria-label')?.trim() || readText(toggle);
  };

  const readDesktopGroup = (link) => {
    const dropdown = link.closest('[data-nav-dropdown]');
    if (!dropdown) return null;

    // Compared as attribute values rather than built into a selector, so no
    // escaping (and no CSS.escape, missing on older browsers) is needed.
    const index = dropdown.getAttribute('data-nav-dropdown');
    const trigger = [...document.querySelectorAll('[data-nav-trigger]')].find(
      (element) => element.getAttribute('data-nav-trigger') === index
    );
    return readText(trigger);
  };

  // Tallies, per menu group, how many of the row's collections it lists; the
  // one holding the most is the row's category. A tally rather than a single
  // lookup because one collection can be linked from more than one place in the
  // menu, while the row as a whole only fits one group.
  const findCategoryIn = (paths, readGroup) => {
    const counts = new Map();

    for (const link of document.querySelectorAll('header a[href], [data-drawer-toggle] ~ ul a[href]')) {
      if (!paths.has(toPath(link.getAttribute('href')))) continue;

      const group = readGroup(link);
      if (group) counts.set(group, (counts.get(group) ?? 0) + 1);
    }

    let best = null;
    for (const [group, count] of counts) {
      if (!best || count > counts.get(best)) best = group;
    }
    return best;
  };

  const categoryCache = new WeakMap();

  const getCategory = (nav) => {
    if (categoryCache.has(nav)) return categoryCache.get(nav);

    const paths = new Set(
      [...nav.querySelectorAll(PILL_SELECTOR)]
        .map((pill) => toPath(pill.getAttribute('href')))
        .filter(Boolean)
    );

    const category = findCategoryIn(paths, readDrawerGroup) ?? findCategoryIn(paths, readDesktopGroup);

    // Only a found value is cached, so a menu that renders late gets another
    // look on the next event.
    if (category) categoryCache.set(nav, category);
    return category;
  };

  // ─── Event pushes ─────────────────────────────────────────────────────────

  const pushEvent = (payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ...payload, client_id: getClientId() });
  };

  // The push is synchronous, ahead of the navigation the link starts, and GTM's
  // GA4 tag sends by beacon, so the hit survives the page unload.
  const onPillClick = (event) => {
    const pill = event.target.closest?.(PILL_SELECTOR);
    if (!pill || !isMweb()) return;

    const nav = event.currentTarget;

    pushEvent({
      event: 'quick_redirect',
      cta_text: readText(pill),
      current_select_value: readText(nav.querySelector(ACTIVE_PILL_SELECTOR)),
      category: getCategory(nav),
    });
  };

  // Nothing is tracked until the row is on screen. The moment it is fully
  // visible, category_navigation_visible fires and only then does the row start
  // listening for taps — so every quick_redirect follows a
  // category_navigation_visible, never the other way round.
  //
  // Once per page view: the row does not come and go, so a second pass back
  // over it is not a second impression.
  const activate = (nav) => {
    if (nav.dataset.dnkTrackingActive) return;
    nav.dataset.dnkTrackingActive = 'true';

    pushEvent({
      event: 'category_navigation_visible',
      category: getCategory(nav),
    });

    nav.addEventListener('click', onPillClick);
  };

  const trackVisibility = (nav) => {
    // Without IntersectionObserver there is no way to tell when the row is on
    // screen, so it is treated as visible straight away rather than never.
    if (!('IntersectionObserver' in window)) {
      if (isMweb()) activate(nav);
      return;
    }

    // On desktop the observer keeps watching instead of giving up, so a window
    // narrowed to mobile width later still gets its impression.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio < FULLY_VISIBLE_RATIO || !isMweb()) continue;

          observer.disconnect();
          activate(nav);
          return;
        }
      },
      { threshold: [FULLY_VISIBLE_RATIO, 1] }
    );

    observer.observe(nav);
  };

  // ─── Init ─────────────────────────────────────────────────────────────────

  const init = () => {
    const existing = document.getElementById(NAV_ID);
    if (existing) {
      trackVisibility(existing);
      return;
    }

    // Convert inserts the row inside the collection-grid section, so that is
    // all that needs watching — and only until the row shows up.
    const root = document.querySelector(SECTION_SELECTOR) ?? document.body;
    const observer = new MutationObserver(() => {
      const nav = document.getElementById(NAV_ID);
      if (!nav) return;

      observer.disconnect();
      trackVisibility(nav);
    });

    observer.observe(root, { childList: true, subtree: true });
  };

  // Exposed for debugging and for GTM, if it would rather compute a parameter
  // itself.
  window.dnkQuickRedirectTracking = { getClientId, getCategory };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
